import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
  DbQuotation,
  QuotationProject,
  QuotationSystem,
  QuotationItem,
} from '../types';
import { getDefaultQuotationParameters } from '../utils/quotationCalculations';
import {
  convertToAllCurrencies,
  type ExchangeRates,
  type Currency,
} from '../utils/currencyConversion';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// CPQ-specific formatting utilities
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function calculateMargin(cost: number, price: number): number {
  return cost > 0 ? ((price - cost) / cost) * 100 : 0;
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

export function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Convert DbQuotation (from Supabase) to QuotationProject (app format)
 */
export function convertDbQuotationToQuotationProject(
  dbQuotation: DbQuotation
): QuotationProject {
  // #21: PostgREST returns nested rows in unspecified order (Postgres heap
  // order, which shifts after any UPDATE). Sort by persisted sort_order
  // here so save→exit→reload is stable. Display order/displayNumber still
  // use index+1 to avoid gaps from deletions.
  const sortedSystems = [...(dbQuotation.quotation_systems || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );

  const systems: QuotationSystem[] = sortedSystems.map((dbSystem, index) => ({
    id: dbSystem.id,
    name: dbSystem.system_name,
    description: dbSystem.system_description,
    order: index + 1,
    quantity: dbSystem.quantity || 1,
    createdAt: dbSystem.created_at,
  }));

  // Convert items if they exist
  const items: QuotationItem[] = [];
  let globalItemOrder = 1;

  sortedSystems.forEach((dbSystem, systemIndex) => {
    const systemOrder = systemIndex + 1;

    const sortedItems = [...(dbSystem.quotation_items || [])].sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
    );

    sortedItems.forEach((dbItem, itemIndex) => {
      const itemOrder = itemIndex + 1;

      // CRITICAL: Use original_currency and original_cost to properly convert prices
      // This ensures that when exchange rates change, we convert from the ORIGINAL currency.
      // #28: We derive all three currencies (NIS/USD/EUR) here rather than reading
      // unit_price_eur off the row — items added before EUR support shipped have
      // unit_price_eur = null, so trusting that field would render €0 for legacy rows.
      const originalCurrency: Currency =
        (dbItem.original_currency as Currency | undefined) || 'NIS';
      const originalCost = dbItem.original_cost || dbItem.unit_cost || 0;
      const rates: ExchangeRates = {
        usdToIlsRate: dbQuotation.exchange_rate || 3.7,
        eurToIlsRate: dbQuotation.eur_to_ils_rate || 4.0,
      };

      const converted = convertToAllCurrencies(
        originalCost,
        originalCurrency,
        rates
      );
      const unitPriceILS = converted.unitCostNIS;
      const unitPriceUSD = converted.unitCostUSD;
      const unitPriceEUR = converted.unitCostEUR;

      items.push({
        id: dbItem.id,
        systemId: dbSystem.id,
        systemOrder,
        itemOrder,
        displayNumber: `${systemOrder}.${itemOrder}`,
        componentId: dbItem.component_id,
        assemblyId: dbItem.assembly_id, // CRITICAL: Preserve assembly link for assembly items
        isCustomItem: dbItem.is_custom_item || false, // Track custom items created directly in quotation
        laborTypeId: (dbItem as { labor_type_id?: string }).labor_type_id, // ✅ Load labor type ID for labor items
        isInternalLabor: (dbItem as { is_internal_labor?: boolean })
          .is_internal_labor, // ✅ Load internal/external labor flag
        componentName: dbItem.item_name,
        componentCategory: dbItem.component?.category || 'כללי',
        itemType: dbItem.item_type || 'hardware',
        laborSubtype: dbItem.labor_subtype,
        quantity: dbItem.quantity || 1,
        unitPriceUSD,
        unitPriceILS,
        unitPriceEUR,
        totalPriceUSD: unitPriceUSD * (dbItem.quantity || 1),
        totalPriceILS: unitPriceILS * (dbItem.quantity || 1),
        itemMarkupPercent:
          dbItem.margin_percentage || dbQuotation.margin_percentage || 25,
        customerPriceILS: dbItem.total_price || 0,
        // #141: `?? undefined` because Postgres hands back `null` for an item
        // with no note, and QuotationItem.notes is `string | undefined` (see
        // .claude/rules/typescript.md rule 1). Without it, "no note" arrives as
        // a null that every reader has to guard separately — the grid cell, the
        // custom-item form, and the delete snapshot each one of them.
        notes: dbItem.notes ?? undefined,
        // CRITICAL: Preserve original currency and cost for exchange rate recalculation
        originalCurrency: originalCurrency,
        originalCost: originalCost,
        // MSRP fields - loaded from quotation item (copied from component when added)
        msrpPrice: dbItem.msrp_price,
        msrpCurrency: dbItem.msrp_currency,
        useMsrpPricing: dbItem.use_msrp_pricing ?? false,
        // #125: written on insert, never read back until now. See the note on
        // QuotationItem — the delete snapshot is derived from that interface,
        // so a column nothing loads is a column a restore cannot reproduce.
        manufacturer: dbItem.manufacturer,
        manufacturerPartNumber: dbItem.manufacturer_part_number,
        partnerDiscountPercent: dbItem.partner_discount_percent,
        createdAt: dbItem.created_at,
        updatedAt: dbItem.updated_at,
      });

      globalItemOrder++;
    });
  });

  // Get default parameters from settings (uses cached values)
  const defaultParams = getDefaultQuotationParameters();

  // Create QuotationProject
  return {
    id: dbQuotation.id,
    name: dbQuotation.project_name || 'הצעת מחיר',
    customerName: dbQuotation.customer_name,
    description: dbQuotation.project_description,
    projectId: dbQuotation.project_id,
    projectName: dbQuotation.project?.project_name || dbQuotation.project_name,
    status:
      dbQuotation.status === 'accepted'
        ? 'won'
        : dbQuotation.status === 'rejected'
          ? 'lost'
          : dbQuotation.status === 'sent'
            ? 'sent'
            : 'draft',
    // #63: the mapping above is lossy — 'expired' collapses onto 'draft'. Keep
    // the real column value so edit-gating can't be fooled by it.
    dbStatus: dbQuotation.status,
    // #153: which quotation this one replaces, so analytics can drop a
    // superseded row without inferring the relationship from version
    // numbers -- `version` says a row IS a revision, never of what.
    // PostgREST hands back null for an original; the field is declared
    // `string | undefined`, so collapse it here rather than letting a null
    // reach a `=== undefined` check somewhere downstream.
    supersedesQuotationId: dbQuotation.supersedes_quotation_id ?? undefined,
    // #69: who created this quotation, so the editor can grant a sales user
    // full rights on their own quotation.
    createdBy: dbQuotation.created_by,
    // #77: the price the rep confirmed sending, if this quotation has been
    // through the confirmation dialog. Keyed on price_confirmed_at rather than
    // on status — a quotation moved to 'sent' by hand from the list grid never
    // went through the dialog and has nothing to report.
    priceConfirmation: dbQuotation.price_confirmed_at
      ? {
          quotedPriceILS: dbQuotation.quoted_price_ils ?? 0,
          systemPriceILS: dbQuotation.system_price_ils ?? 0,
          confirmedAt: dbQuotation.price_confirmed_at,
          confirmedBy: dbQuotation.price_confirmed_by,
        }
      : undefined,
    createdAt: dbQuotation.created_at,
    updatedAt: dbQuotation.updated_at,
    systems,
    parameters: {
      // #147: `||` or `??`, decided per field rather than swapped wholesale.
      //
      // The two operators differ here on exactly one input — a stored 0 — so
      // the question per field is only ever "is zero a setting a user chose,
      // or is it a value nothing can be priced with?".
      //
      // `||` (a stored 0 falls back to the team default):
      //   Both exchange rates multiply every converted line, so a zero rate
      //   would price the whole quotation at nothing. `markupPercent` is a
      //   *divisor* — `computeLinePrice` does cost ÷ coefficient — and the
      //   parameters panel enforces min="0.01" on it, so the UI cannot even
      //   produce a zero. `resolveCoefficient` already refuses 0 downstream,
      //   so `??` here would only make the panel display a markup the pricing
      //   does not use.
      //
      // `??` (a stored 0 is honoured):
      //   The risk input is min="0" and says so — "ערך בין 0 ל-20 אחוז" — and
      //   a quotation may genuinely carry no risk premium. The day rate is
      //   min="0" too, and internal labour thrown in at no charge is an
      //   ordinary thing to quote. Both are multiplied, never divided by.
      //
      // Same question as the #77 discount fields below, third answer: those
      // have no team default at all, so they fall through to undefined.
      usdToIlsRate: dbQuotation.exchange_rate || defaultParams.usdToIlsRate,
      eurToIlsRate: dbQuotation.eur_to_ils_rate || defaultParams.eurToIlsRate,
      markupPercent:
        dbQuotation.margin_percentage || defaultParams.markupPercent,
      dayWorkCost: dbQuotation.day_work_cost ?? defaultParams.dayWorkCost,
      profitPercent: defaultParams.profitPercent,
      riskPercent: dbQuotation.risk_percentage ?? defaultParams.riskPercent,
      // #77: the discount is per-quotation and has no team default — a
      // quotation with no discount must load as having no discount, so these
      // fall through to undefined rather than to `defaultParams`.
      // `?? undefined` collapses the column's null back to the undefined the
      // parameters type uses; the two mean the same thing here (no discount).
      discountType: dbQuotation.discount_type ?? undefined,
      discountValue: dbQuotation.discount_value ?? undefined,
      includeVAT: defaultParams.includeVAT,
      vatRate: defaultParams.vatRate,
      paymentTerms: dbQuotation.terms || defaultParams.paymentTerms,
      deliveryTime: defaultParams.deliveryTime,
    },
    items,
    calculations: {
      totalHardwareUSD: 0,
      totalHardwareILS: 0,
      totalSoftwareUSD: 0,
      totalSoftwareILS: 0,
      totalLaborUSD: 0,
      totalLaborILS: 0,
      totalEngineeringILS: 0,
      totalIntegrationILS: 0,
      totalDevelopmentILS: 0,
      totalInstallationILS: 0,
      subtotalUSD: 0,
      subtotalILS: 0,
      totalCustomerPriceILS: dbQuotation.total_price || 0,
      riskAdditionILS: 0,
      totalQuoteILS: dbQuotation.total_price || 0,
      // #77: placeholders like every other field in this block — the real
      // figures arrive when calculateQuotationTotals runs over the loaded
      // items. netQuote mirrors totalQuote here because a zero discount is the
      // only thing this stub can honestly claim.
      discountILS: 0,
      netQuoteILS: dbQuotation.total_price || 0,
      totalVATILS: 0,
      finalTotalILS: dbQuotation.total_price || 0,
      totalCostILS: dbQuotation.total_cost || 0,
      totalProfitILS:
        (dbQuotation.total_price || 0) - (dbQuotation.total_cost || 0),
      profitMarginPercent: dbQuotation.total_cost
        ? (((dbQuotation.total_price || 0) - dbQuotation.total_cost) /
            dbQuotation.total_cost) *
          100
        : 0,
    },
  };
}
